import { mdiLanDisconnect, mdiRefresh } from "@mdi/js";
import "@timmo001/groundwork-ui/gw-alert.ts";
import "@timmo001/groundwork-ui/gw-button.ts";
import "@timmo001/groundwork-ui/gw-card.ts";
import "@timmo001/groundwork-ui/gw-checkbox.ts";
import "@timmo001/groundwork-ui/gw-empty-state.ts";
import "@timmo001/groundwork-ui/gw-facts.ts";
import "@timmo001/groundwork-ui/gw-input.ts";
import "@timmo001/groundwork-ui/gw-page-header.ts";
import "@timmo001/groundwork-ui/gw-setting-row.ts";
import "@timmo001/groundwork-ui/gw-skeleton.ts";
import { html, LitElement, nothing, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";

import { pageStyles, shared } from "~/components/shared-styles";
import { AtomController } from "~/controllers/atom-controller";
import {
  type ConnectionStatus,
  connectionStatus,
  retryConnection,
} from "~/lib/atoms";
import { documentTitle } from "~/lib/navigation";
import { href } from "~/lib/router";

export interface PageHeader {
  readonly heading: string;
  readonly description?: string;
  /** An `@mdi/js` path for the tile before the heading. */
  readonly icon?: string;
  /** Controls at the end of the header. */
  readonly actions?: TemplateResult;
}

/**
 * The base for every page: shadow DOM with the shared styles, the browser
 * tab's title, a common header, and a gate for content that needs the
 * connection. A page can also be a section of another, such as the commands
 * on the System page.
 */
export abstract class SbPage extends LitElement {
  static override styles = [shared, pageStyles];

  /** Shown as a section of another page: an h2 heading, and no tab title. */
  @property({ type: Boolean, reflect: true }) embedded = false;

  readonly #status = new AtomController(this, () => connectionStatus);

  /** The page's name for the browser tab. */
  protected abstract readonly pageTitle: string;

  /** Cards go a level under the section heading when embedded. */
  protected get cardLevel(): number {
    return this.embedded ? 3 : 2;
  }

  protected get status(): ConnectionStatus {
    return this.#status.value;
  }

  protected override updated(): void {
    if (!this.embedded) document.title = documentTitle(this.pageTitle);
  }

  protected renderHeader(header: PageHeader): TemplateResult {
    return html`
      <gw-page-header
        level=${this.embedded ? 2 : 1}
        heading=${header.heading}
        description=${header.description ?? ""}
        icon=${header.icon ?? ""}
      >
        ${
          header.actions === undefined
            ? nothing
            : html`<div slot="actions" class="actions">${header.actions}</div>`
        }
      </gw-page-header>
    `;
  }

  /** The outcome of an action, from `actionResult`. */
  protected renderResult(
    result: { readonly success: boolean; readonly message: string } | null,
    extra?: TemplateResult,
  ): TemplateResult | typeof nothing {
    if (result === null) return nothing;

    return html`
      <gw-alert
        tone=${result.success ? "success" : "danger"}
        ?urgent=${!result.success}
        heading=${result.success ? "Done" : "That didn't work"}
      >
        ${result.message} ${extra ?? nothing}
      </gw-alert>
    `;
  }

  /**
   * Shows `content` once connected. While the connection is still being
   * made, shows `placeholder`, which should take up the same room as the
   * content so nothing moves when it arrives. Only a failed connection shows
   * the prompt to set one up.
   */
  protected gate(
    content: () => TemplateResult,
    placeholder: TemplateResult,
  ): TemplateResult {
    const { isConnected, error } = this.status;

    if (isConnected) return content();

    if (error === null) return placeholder;

    return html`
      <gw-card role="alert">
        <gw-empty-state
          icon=${mdiLanDisconnect}
          heading="Not connected to System Bridge"
        >
          <p>${error}</p>
          <div slot="actions" class="row wrap" style="justify-content: center">
            <gw-button href=${href("connection")}
              >Connection settings</gw-button
            >
            <gw-button
              appearance="filled"
              icon=${mdiRefresh}
              @click=${retryConnection}
              >Retry</gw-button
            >
          </div>
        </gw-empty-state>
      </gw-card>
    `;
  }
}
